import {Sprite as PixiSprite} from 'pixi.js';
import {Component, ComponentProps} from '@gongxigames/framework/core/fragments/BuilderX/components/Component';
import {Button} from '@gongxigames/framework/core/fragments/BuilderX/components/Button';
import {Graphic} from '@gongxigames/framework/core/fragments/BuilderX/components/Graphic';
import {Image} from '@gongxigames/framework/core/fragments/BuilderX/components/Image';
import {MovieClip} from '@gongxigames/framework/core/fragments/BuilderX/components/MovieClip';
import {Sprite} from '@gongxigames/framework/core/fragments/BuilderX/components/Sprite';
import {Text} from '@gongxigames/framework/core/fragments/BuilderX/components/Text';
import {TweenAnimation} from '@gongxigames/framework/core/fragments/BuilderX/components/TweenAnimation';
import {FrameAnimation} from '@gongxigames/framework/core/fragments/BuilderX/components/FrameAnimation';
import {RichText} from '@gongxigames/framework/core/fragments/BuilderX/components/RichText';
import {Context, DataBridge, Platform, Resources} from '@gongxigames/framework/core/fragments/BuilderX/types';
import {Animation} from '@gongxigames/framework/core/fragments/display';

type ResourcesType = 'shared_sprite'|'game_sprite';
type AllPlatformsConfig = Platform |'shared';

const JSON_COMPONENTS_MAP: Record<string, any> = {
    sprite: Sprite,
    button: Button,
    graphic: Graphic,
    image: Image,
    movieclip: MovieClip,
    text: Text,
    rich_text: RichText,
    tween_animation: TweenAnimation,
    frame_animation: FrameAnimation
};

export type BuilderXBuildOptions<T> = {
    element?: T;
    forceKeepElement?: boolean;
};

export class BuilderX {
    private _context?: Context;

    /* develblock:start */
    private liveIdAttachments: Record<string, Set<PixiSprite>> = {};
    /* develblock:end */

    private readonly components = {
        shared_sprite: {
            desktop: {} as Record<string, Component<ComponentProps>>,
            mobile: {} as Record<string, Component<ComponentProps>>,
            shared: {} as Record<string, Component<ComponentProps>>
        },
        game_sprite: {
            desktop: {} as Record<string, Component<ComponentProps>>,
            mobile: {} as Record<string, Component<ComponentProps>>,
            shared: {} as Record<string, Component<ComponentProps>>
        } as const
    };

    public jsx<C extends typeof Component>(
        constructor: C,
        props: Record<string, any>,
        ...children: JSX.Element[]
    ): Component<ComponentProps> {

        children = children.flat();

        // @ts-expect-error
        const component = new constructor({...props, children}) as Component<ComponentProps>;

        /* develblock:start */
        if (this._context && props?.reloadId) {
            this.liveIdAttachments[props.reloadId]?.forEach(element => {
                component.build({context: this._context!, element});
            });
        }
        /* develblock:end */

        return component;
    }

    public register(
        name: string,
        component: JSX.Element,
        platform: AllPlatformsConfig = 'shared',
        resourceType: ResourcesType = 'game_sprite'
    ): void {
        this.components[resourceType][platform][name] = component as any;
    }

    public registerJson(
        json: any,
        rootName: ResourcesType
    ): void {
        Object
            .entries(json)
            .forEach(([name, json]) => {
                this.components[rootName].shared[name] = this.getComponentFromJson(json);
            });
    }

    public setContext(bridge: DataBridge): void {
        this._context = {
            ...bridge,
            attachToReloadId: this.attachToReloadId.bind(this)
        };
    }

    public updateResources(): void {
        this._context!.updateResources(this.getResources()!);
    }

    public build<P extends ComponentProps, T extends PixiSprite|Animation>(
        item: Component<P, T> | Readonly<Component<P, T>>,
        options?: BuilderXBuildOptions<T>
    ): T {
        return item.build({
            ...options,
            context: this._context!
        });
    }

    private getComponentFromJson<R extends Component<ComponentProps>, X extends typeof Component>(json: any): R {
        const children = json.children?.map((item: any) => this.getComponentFromJson(item)) || [];
        const frames = json.frames?.map((item: any) => this.getComponentFromJson(item)) || [];

        const Constructor = JSON_COMPONENTS_MAP[json.type] as X;
        const props = Constructor.propsFromJson(json);

        props.children = [...children, ...frames];

        // @ts-expect-error
        return new Constructor(props);
    }

    private componentToJson(component: Component<ComponentProps>): any {
        return component.propsToJson();
    }

    private getResources(): Resources | undefined {
        if (!this._context) {
            return;
        }

        const platform = this._context.getPlatform();

        return {
            shared_sprite: {...this.components.shared_sprite.shared, ...this.components.shared_sprite[platform]},
            game_sprite: {...this.components.game_sprite.shared, ...this.components.game_sprite[platform]}
        };
    }

    private attachToReloadId<T extends PixiSprite>(id: string, item: T): void {
        /* develblock:start */
        if (!this.liveIdAttachments[id]) {
            this.liveIdAttachments[id] = new Set();
        }

        item.on('added', () => {
            this.liveIdAttachments[id].add(item);
        });

        item.on('removed', () => {
            this.liveIdAttachments[id].delete(item);
        });
        /* develblock:end */
    }
}

export const builderX = new BuilderX();
